// src/utils/api.js
// Cliente HTTP hacia el backend real (Slim PHP + MySQL).
import axios from 'axios';

export const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8090/api';

const http = axios.create({ baseURL: API_BASE_URL });

http.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

http.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !window.location.pathname.startsWith('/login')) {
      ['token', 'id_doctor', 'hotel_id', 'hotel_nombre', 'tipo_sesion'].forEach((k) => localStorage.removeItem(k));
      window.location.href = '/login';
    }
    const mensaje = error.response?.data?.error || 'No se pudo completar la solicitud.';
    return Promise.reject(new Error(mensaje));
  }
);

// ── SERVICIOS ADICIONALES (catálogo estático, no cambia con frecuencia) ────
export const SERVICIOS_ADICIONALES = [
  { clave: 'segundo_paciente', concepto: 'Atención a un segundo paciente no agendado', precio: null, precioTexto: '75% del total de la primera consulta' },
  { clave: 'recetas_adicionales', concepto: 'Emisión de recetas adicionales o con características especiales', precio: 100 },
  { clave: 'curacion_simple', concepto: 'Curación simple', precio: 250 },
  { clave: 'retiro_puntos', concepto: 'Retiro de puntos', precio: 250 },
  { clave: 'medicamento_inyectable', concepto: 'Aplicación de medicamento inyectable (sin medicamento)', precio: 120 },
  { clave: 'sutura_simple', concepto: 'Sutura simple (hasta 3 cm)', precio: 600 },
  { clave: 'sutura_intermedia', concepto: 'Sutura intermedia (3-5 cm)', precio: 800 },
  { clave: 'sutura_mayor', concepto: 'Sutura mayor (más de 5 cm)', precio: 1000 },
];

// ── AUTENTICACIÓN ───────────────────────────────────────────────────────
export async function login(usuario, contraseña) {
  const { data } = await http.post('/auth/login', { usuario, contraseña });
  return data;
}

export async function getMe() {
  const { data } = await http.get('/auth/me');
  return data;
}

export async function verifyPassword(contraseña) {
  const { data } = await http.post('/auth/verify-password', { contraseña });
  return data.valido;
}

// ── USUARIOS (staff) ────────────────────────────────────────────────────
export async function getUsuarios() {
  const { data } = await http.get('/usuarios');
  return data;
}

export async function getUsuarioById(id) {
  const { data } = await http.get(`/usuarios/${id}`);
  return data;
}

export async function addUsuario(datos) {
  const { data } = await http.post('/usuarios', datos);
  return data;
}

export async function updateUsuario(id, nuevosDatos) {
  if (typeof FormData !== 'undefined' && nuevosDatos instanceof FormData) {
    const { data } = await http.post(`/usuarios/${id}`, nuevosDatos);
    return data;
  }
  const { data } = await http.patch(`/usuarios/${id}`, nuevosDatos);
  return data;
}

export async function setUsuarioActivo(id, activo) {
  const { data } = await http.patch(`/usuarios/${id}/activo`, { activo });
  return data;
}

export async function uploadDocumentosUsuario(id, datos) {
  const { data } = await http.post(`/usuarios/${id}/documentos`, datos);
  return data;
}

// ── HOTELES (Aliados) ───────────────────────────────────────────────────
export async function getHoteles() {
  const { data } = await http.get('/hoteles');
  return data;
}

export async function addHotel(datos) {
  const { data } = await http.post('/hoteles', datos);
  return data;
}

export async function setHotelActivo(id, activo) {
  const { data } = await http.patch(`/hoteles/${id}/activo`, { activo });
  return data;
}

export async function updateHotel(id, nuevosDatos) {
  const { data } = await http.patch(`/hoteles/${id}`, nuevosDatos);
  return data;
}

export async function actualizarCredencialesHotel(id, adminPassword, usuario, contraseña) {
  const { data } = await http.patch(`/hoteles/${id}/credenciales`, { admin_password: adminPassword, usuario, contraseña });
  return data;
}

// ── CONSULTAS ────────────────────────────────────────────────────────────
export async function getConsultas() {
  const { data } = await http.get('/consultas');
  return data;
}

// El backend ya filtra por el doctor de la sesión cuando no es administrador.
export async function getConsultasByDoctor() {
  return getConsultas();
}

export async function getConsultaByFolio(folio) {
  try {
    const { data } = await http.get(`/consultas/${encodeURIComponent(folio)}`);
    return data;
  } catch (error) {
    return null;
  }
}

export async function folioExiste(folio) {
  return (await getConsultaByFolio(folio)) !== null;
}

export async function addConsulta(consulta) {
  const { data } = await http.post('/consultas', consulta);
  return data;
}

export async function updateConsulta(folio, nuevosDatos) {
  const { data } = await http.patch(`/consultas/${encodeURIComponent(folio)}`, nuevosDatos);
  return data;
}

// ── IDIOMAS ──────────────────────────────────────────────────────────────
export async function getIdiomas() {
  const { data } = await http.get('/idiomas');
  return data;
}

export async function addIdioma(nombre) {
  const { data } = await http.post('/idiomas', { nombre });
  return data;
}

// ── ENCUESTA DE SATISFACCIÓN ─────────────────────────────────────────────
export async function getEncuestas() {
  const { data } = await http.get('/encuestas');
  return data;
}

export async function addEncuesta(respuestas) {
  const { data } = await http.post('/encuestas', respuestas);
  return data;
}

// ── CONFIGURACIÓN ────────────────────────────────────────────────────────
export async function getDireccionReceta() {
  const { data } = await http.get('/config/direccion-receta');
  return data.direccion;
}

export async function setDireccionReceta(direccion) {
  const { data } = await http.put('/config/direccion-receta', { direccion });
  return data.direccion;
}

// ── ANALÍTICAS ───────────────────────────────────────────────────────────
export async function getConsultasPorIdiomaYMes(anio) {
  const { data } = await http.get('/analiticas/consultas-por-idioma-mes', { params: { anio } });
  return data;
}
